import {ChronoUnit} from "@js-joda/core";
import {useGetDataInfoRange, useGetDatasetInfo} from "api/api-hooks/datasets-hooks";
import {DatasetDataApi} from "api/DatasetDataApi";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import {UpdateDatasetData} from "api/model/requests";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import {ICombinedData} from "components/dashboard/data-input-output-table/common/api/interfaces/interfaces";
import {getExtractedDataValue, remakeArrayIntoOtherInterface} from "components/dashboard/data-input-output-table/utils";
import {
    calculateStepTime,
    DatasetTypes,
    formatDateBasedOnDataType
} from "components/dashboard/dataset-window/utils/utils";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {TitleOverrideAndDescriptionTemplate} from "components/templates/DataTableCellTemplates";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import React, {useEffect, useState} from "react";
import {addToDate, ChronoUnitLegacy} from "utils/time-utils";

type AddDataDialogProps = {
    selectedDatasetID: any;
    firstLoadDate: Date;
    dateFrequency: ChronoUnitLegacy;
    closeDialog(): void;
};

const WARNING_LIMIT = 100;

const AddDataDialog = ({selectedDatasetID, firstLoadDate, dateFrequency, closeDialog}: AddDataDialogProps) => {
    const identifiers = selectedDatasetID.map((ds: DatasetInfo) => ds.identifier);
    const queryData = useGetDatasetInfo(identifiers);

    const datasetType: DatasetTypes = queryData && queryData.isSuccess ? queryData.data!.dataType as unknown as DatasetTypes : "HOURLY";

    const [dataQuery, setDataQuery] = useState<Array<any>>([]);

    const [rangeSelected, setRangeSelected] = useState(false);
    const [selectedTime, setSelectedTime] = useState<ChronoUnit>(ChronoUnit.MINUTES);
    const [timeValue, setTimeValue] = useState(0);
    const [dateFrom, setDateFrom] = useState<Date>(addToDate(new Date(), -6, "HOURS"));
    const [dateTo, setDateTo] = useState<Date>(new Date());
    const [valueCount, setValueCount] = useState<number>(0);

    const fetchQuery = useGetDataInfoRange(
        identifiers,
        {timeStart: dateTo.toISOString() || firstLoadDate.toISOString()},
        {
            enabled: dateTo && identifiers.length > 0,
        }
    );

    useEffect(() => {
        if (datasetType) {
            setDateFrom(addToDate(formatDateBasedOnDataType(datasetType), -6, "HOURS"));
            setDateTo(formatDateBasedOnDataType(datasetType));
        }

    }, [datasetType]);

    useEffect(() => {
        const from = sessionStorage.getItem("dateFrom");
        const to = sessionStorage.getItem("dateTo");
        if (from) setDateFrom(new Date(from));
        if (to) setDateTo(new Date(to));
    }, []);

    const titleColumnRender = (rowData: ICombinedData) => {
        return (
            <div className="flex gap-2">
                {TitleOverrideAndDescriptionTemplate({
                    title: rowData.title,
                    overriddenTitle: rowData.overriddenTitle,
                    description: rowData.description,
                    identifier: rowData.identifier,
                })}
                <ChartInDialogButton identifier={rowData.identifier}/>
            </div>
        );
    };

    useEffect(() => {
        if (fetchQuery.data && !fetchQuery.isError && !fetchQuery.isLoading && fetchQuery.data?.length > 0) {
            setDataQuery([...remakeArrayIntoOtherInterface(fetchQuery.data, selectedDatasetID)]);
        }
    }, [fetchQuery.data]);

    useEffect(() => {
        fetchQuery.refetch();
    }, [dateTo]);

    const updateHandler = () => {
        if (dataQuery[0].data.length > 0) {
            if (valueCount < WARNING_LIMIT) {
                updateData();
                return;
            }
            confirmDialog({
                message: `Do you want to add ${valueCount} points?`,
                header: "Confirmation",
                icon: "pi pi-exclamation-triangle",
                accept: () => updateData(),
            });
        } else {
            easasAlert({ summary: "Warn", detail: "No changes made", severity: "warn" });
        }
    };

    const updateData = () => {
        const tempArr: Array<UpdateDatasetData | undefined> = dataQuery.map((rowData) => {
            const tempOb = fetchQuery.data?.find((ob: any) => ob.identifier === rowData.identifier);

            if (tempOb && rowData.data.length > 0) {
                if (tempOb.data.length === 0 && !rowData.data[0].value && rowData.data[0].value !== 0) {
                    return;
                }
                if (tempOb.data.length > 0 && tempOb.data[0].value === rowData.data[0].value) {
                    return;
                }

                const objVal: {
                    [k: string]: any
                } = {};

                if (rangeSelected) {
                    if (rowData.data.length > 0) {
                        for (let i = 0; i < rowData.data.length; i++) {
                            objVal[`${rowData.data[i].key}`] = rowData.data[i].value;
                        }
                    }
                } else {
                    let dateString = dateTo.toISOString();
                    if (rowData.data.length > 0) {
                        dateString = rowData.data[0].key;
                    }
                    objVal[`${dateString}`] = rowData.data[0].value;
                }
                return {
                    identifier: rowData.identifier,
                    values: objVal,
                };
            }
        });

        if (tempArr.length === 0) {
            easasAlert({ summary: "Warn", detail: "No changes made", severity: "warn" });
            return;
        }

        DatasetDataApi.updateData(tempArr as Array<UpdateDatasetData>)
            .then(() => {
                easasAlert({ summary: "Success", detail: "Datasets updated", severity: "success" });
            })
            .catch((er) => {
                console.error(er);
                easasAlert({ summary: "Error", detail: "Error while updating datasets", severity: "error" });
                closeDialog();
            });
    };

    const valueBodyInputTemplate = (rowData: ICombinedData) => {
        let suffix = "";

        if (rowData.unit.length > 0 && rowData.unit !== " ") {
            suffix = ` ${  rowData.unit}`;
        }

        return (
            <InputNumber
                value={getExtractedDataValue(rowData.data, rowData.decimalPlaces)}
                onValueChange={(e) => valueChangeHandler(rowData.identifier as string, e.value!)}
                mode="decimal"
                minFractionDigits={rowData.decimalPlaces}
                maxFractionDigits={rowData.decimalPlaces}
                suffix={suffix}
                placeholder={suffix}
            />
        );
    };

    const valueChangeHandler = (identifier: string, newValue: number) => {
        const tempArr = dataQuery.map((item) => {
            if (item.identifier === identifier) {
                if (rangeSelected) {
                    const dateFromForMonths = new Date(dateFrom.getTime());
                    let calculateFrom = dateFrom.getTime();
                    const calculateTo = dateTo.getTime();
                    if (timeValue > 0) {
                        const step = calculateStepTime(timeValue, selectedTime);
                        const dataArray = [];
                        if (selectedTime === ChronoUnit.MONTHS) {
                            while (dateFromForMonths.getTime() <= calculateTo) {
                                dataArray.push({key: `${dateFromForMonths.toISOString()}`, value: newValue});
                                dateFromForMonths.setMonth(dateFromForMonths.getMonth() + step);
                            }
                        } else {
                            while (calculateFrom <= calculateTo) {
                                dataArray.push({key: `${new Date(calculateFrom).toISOString()}`, value: newValue});
                                calculateFrom += step;
                            }
                        }

                        return {
                            ...item,
                            data: dataArray,
                        };
                    }
                } else {
                    return {
                        ...item,
                        data: item.data.length > 0 ? [{
                            ...item.data[0],
                            value: newValue
                        }] : [{key: `${dateTo.toISOString()}`, value: newValue}],
                    };
                }
            }
            return item;
        });

        setDataQuery(tempArr);
    };

    useEffect(() => {
        let calculateFrom = dateFrom.getTime();
        const calculateTo = dateTo.getTime();
        if (timeValue > 0) {
            const step = calculateStepTime(timeValue, selectedTime);
            let count = 0;
            if (selectedTime === ChronoUnit.MONTHS) {
                const monthsDiff = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12 + dateTo.getMonth() - dateFrom.getMonth();
                if (dateTo.getDate() < dateFrom.getDate()) {
                    count = monthsDiff - 1;
                }
                count = monthsDiff;
            } else {
                while (calculateFrom <= calculateTo) {
                    calculateFrom += step;
                    count++;
                }
            }
            setValueCount(count);
        }
    }, [timeValue, selectedTime]);

    const dateFromHandler = (e: Date) => {
        setDateFrom(e);
        sessionStorage.setItem("dateFrom", e.toISOString());
    };

    const dateToHandler = (e: Date) => {
        setDateTo(e);
        sessionStorage.setItem("dateTo", e.toISOString());
    };

    return (
        <>
            {fetchQuery.isError && <>Error...</>}

            {!fetchQuery.isError && (
                <div className="flex flex-column gap-3 mt-2">
                    <div className="flex gap-3 align-items-center">
                        <p className="mb-0">Range</p>
                        <InputSwitch checked={rangeSelected} onChange={(e) => setRangeSelected(e.value)}/>
                    </div>

                    <div>
                        {rangeSelected &&
                            <CalendarWrapper
                                id="dateFrom"
                                showSeconds
                                value={dateFrom}
                                onChange={dateFromHandler}
                                dateFrequency={dateFrequency}
                            />}
                        <CalendarWrapper
                            id="dateTo"
                            showSeconds
                            value={dateTo}
                            onChange={dateToHandler}
                            dateFrequency={dateFrequency}
                        />
                    </div>
                    {rangeSelected && (
                        <div className="flex gap-2 align-items-center">
                            <p className="mb-0">Every: </p>
                            <InputNumber
                                className="max-w-max"
                                value={timeValue}
                                onValueChange={(e) => setTimeValue(e.value as number)}
                            />
                            <TimeUnitDropdown value={selectedTime} onChange={(e) => setSelectedTime(e.value)} disabledValue={ChronoUnit.YEARS}/>

                            <div>Expected values count: {valueCount}</div>
                        </div>
                    )}

                    <DataTable
                        value={dataQuery}
                        editMode="cell"
                        className="editable-cells-table"
                        responsiveLayout="scroll"
                        scrollHeight="50vh"
                        loading={fetchQuery.isLoading}
                        size="small"
                    >
                        <Column field="title" header="Dataset" body={titleColumnRender} style={{width: "90%"}}/>
                        <Column field="unit" header="Value" body={valueBodyInputTemplate}/>
                    </DataTable>
                    <div className="button-container flex gap-2">
                        <Button
                            label="Update"
                            icon="pi pi-pencil"
                            onClick={updateHandler}
                            className="p-button-success p-button-rounded p-button-sm"
                        />
                        <ConfirmDialog/>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => closeDialog()}
                            className="p-button-danger p-button-rounded p-button-sm"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default AddDataDialog;
